import { MouseController } from './MouseController';
import type { Simulation } from '../core/simulation/Simulation';
import type { Renderer } from '../renderers/base/Renderer';
import { SandTool } from './tools/SandTool';
import { WaterTool } from './tools/WaterTool';
import type { Coordinate } from '../core/types/SimulationTypes';

export type ToolType = 'sand' | 'water';

export class InputManager {
  private mouseController: MouseController;
  private sandTool: SandTool;
  private waterTool: WaterTool;
  private currentTool: ToolType = 'sand';
  private currentRenderer: Renderer | null = null;
  private hoverCoordinate: Coordinate | null = null;

  constructor(container: HTMLElement) {
    this.mouseController = new MouseController(container);
    this.sandTool = new SandTool();
    this.waterTool = new WaterTool();

    this.setupListeners();
  }

  private setupListeners(): void {
    this.mouseController.on('mousedown', (state, event) => {
      // Only override mode with mouse buttons if not explicitly set via UI
      if (this.currentTool === 'sand') {
        if (state.button === 'left') {
          this.sandTool.setMode('raise');
        } else if (state.button === 'right') {
          this.sandTool.setMode('lower');
        }
      }
    });

    this.mouseController.on('mousemove', () => {});

    window.addEventListener('keydown', this.handleKeyDown);
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === '1') {
      this.setTool('sand');
      console.log('Switched to Sand Tool');
    } else if (event.key === '2') {
      this.setTool('water');
      console.log('Switched to Water Tool');
    } else if (this.currentRenderer) {
      const panSpeed = 20;
      const zoomSpeed = 0.1;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          this.currentRenderer.pan(-panSpeed, 0);
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.currentRenderer.pan(panSpeed, 0);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.currentRenderer.pan(0, -panSpeed);
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.currentRenderer.pan(0, panSpeed);
          break;
        case '+':
        case '=':
          event.preventDefault();
          this.currentRenderer.zoom(zoomSpeed);
          break;
        case '-':
        case '_':
          event.preventDefault();
          this.currentRenderer.zoom(-zoomSpeed);
          break;
      }
    }
  };

  public update(simulation: Simulation, renderer: Renderer): void {
    this.currentRenderer = renderer;
    const mouseState = this.mouseController.getState();

    // Update hover coordinate
    this.hoverCoordinate = renderer.screenToWorld(
      mouseState.position.x,
      mouseState.position.y
    );

    if (mouseState.isDragging) {
      const worldCoord = renderer.screenToWorld(
        mouseState.position.x,
        mouseState.position.y
      );

      if (worldCoord) {
        if (this.currentTool === 'sand') {
          this.sandTool.apply(simulation, worldCoord);
        } else if (this.currentTool === 'water') {
          this.waterTool.apply(simulation, worldCoord);
        }
      }
    }
  }

  public setTool(tool: ToolType): void {
    this.currentTool = tool;
  }

  public getCurrentTool(): ToolType {
    return this.currentTool;
  }

  public getMouseController(): MouseController {
    return this.mouseController;
  }

  public getSandTool(): SandTool {
    return this.sandTool;
  }

  public getWaterTool(): WaterTool {
    return this.waterTool;
  }

  public getHoverCoordinate(): Coordinate | null {
    return this.hoverCoordinate;
  }

  public getCurrentBrushSize(): number {
    if (this.currentTool === 'sand') {
      return this.sandTool.getBrushSize();
    } else {
      return this.waterTool.getBrushSize();
    }
  }

  public dispose(): void {
    window.removeEventListener('keydown', this.handleKeyDown);
    this.mouseController.dispose();
  }
}
